/**
 * Pure step-plan logic for `ContactModal`, extracted so the (many interacting)
 * conditions behind the dynamic step count are unit-testable in the node test
 * env. The modal renders 1 or 2 steps; step 2 (the location picker) exists only
 * when the ds2026 flow is on, the company can be scoped to locations, and the
 * user chose "Select locations". Legacy (non-ds2026) keeps its own `includeDoorSelection` gate.
 */

export interface ContactModalStepInputs {
  /** ds2026 (fb-ui) flow on. */
  ds2026: boolean
  /** The chosen company's id (0/undefined = none / not yet an existing company). */
  selectedContactCompanyId: number | null | undefined
  /** Location count from the company search result (instant; 0 if unknown). */
  companyLocationCount: number
  /** Door count from the fallback count fetch (edit flow). */
  companyTotalDoors: number
  /** The step-1 scope radio value. */
  applyTo: 'entire_company' | 'select_locations' | undefined
  /** Legacy (non-ds2026) door-step gate, threaded from the caller. */
  includeDoorSelection: boolean
}

export interface ContactModalStepPlan {
  /** Whether the step-1 scope radio should be offered (multi-location company). */
  canSelectLocations: boolean
  /** Whether the location-picker step exists. */
  showDoorStep: boolean
  /** Total step count driving the Next/Create footer + progress. */
  totalSteps: 1 | 2
}

export const getContactModalStepPlan = ({
  ds2026,
  selectedContactCompanyId,
  companyLocationCount,
  companyTotalDoors,
  applyTo,
  includeDoorSelection,
}: ContactModalStepInputs): ContactModalStepPlan => {
  // Offered only for an existing company (real id) with more than one location.
  // The search result's count is instant; the fetched door count is the edit-
  // flow fallback. Keep in sync with `showApplyToRadio` in step 1.
  const canSelectLocations =
    !!selectedContactCompanyId &&
    selectedContactCompanyId > 0 &&
    (companyLocationCount > 1 || companyTotalDoors > 1)

  const showDoorStep = ds2026
    ? canSelectLocations && applyTo === 'select_locations'
    : includeDoorSelection

  return { canSelectLocations, showDoorStep, totalSteps: showDoorStep ? 2 : 1 }
}
